import React from "react";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import { alpha } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { RANGE_PRESET, getRangeHelperText } from "./reportBuilders/dailyCareReportEntryUtils";
import { DailyCareReportDateRangeControls } from "./DailyCareReportSections";

export const DailyCareReportRangeSelector = ({
  rangePreset,
  setRangePreset,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  periodLabel,
}) => (
  <Box>
    <DailyCareReportDateRangeControls
      rangePreset={rangePreset}
      setRangePreset={setRangePreset}
    />
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
      {[periodLabel, getRangeHelperText(rangePreset)].filter(Boolean).join(" · ")}
    </Typography>
    {rangePreset === RANGE_PRESET.CUSTOM ? (
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1.5 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start"
            value={customStartDate}
            onChange={(value) => value && setCustomStartDate(value)}
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
          />
          <DatePicker
            label="End"
            value={customEndDate}
            onChange={(value) => value && setCustomEndDate(value)}
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
          />
        </LocalizationProvider>
      </Stack>
    ) : null}
  </Box>
);

const DailyCareReportHeader = ({
  title = "Care Team Update",
  childName,
  periodLabel,
  rangePreset,
  setRangePreset,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  onBack,
  onDownloadPdf,
  onCopy,
  onShare,
  onOpenFilters,
  showActions = true,
  showRangeControls = true,
  theme,
}) => {
  // Functional icon-button chrome - always the fixed semantic sage tokens,
  // not the per-child identity accent (this header isn't identity UI).
  const accentPrimary = theme.palette.semantic?.sage || theme.palette.primary.main;
  const accentInk = theme.palette.semantic?.sageDeep || theme.palette.primary.dark;
  const accentSoft = theme.palette.semantic?.sageMist || alpha(accentPrimary, 0.14);

  return (
  <Box
    className="therapy-app-header"
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 10,
      px: { xs: 1.5, sm: 2.5 },
      pt: { xs: 0.75, sm: 0.95 },
      pb: 0.65,
      bgcolor: alpha(theme.palette.background.paper, 0.78),
      backdropFilter: "blur(14px)",
      borderBottom: `1px solid ${alpha(theme.palette.semantic?.line || theme.palette.text.primary, 0.7)}`,
      "@media print": {
        display: "none",
      },
    }}
  >
    <Container maxWidth={false} sx={{ maxWidth: "8.5in", px: "0 !important" }}>
      <Box
        sx={{
          px: { xs: 0, sm: 0 },
          py: { xs: 0.35, sm: 0.55 },
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "48px minmax(0, 1fr) auto", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={onBack}
            aria-label="Back to dashboard"
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              bgcolor: alpha(accentSoft, 0.76),
              color: accentInk,
              "&:hover": {
                bgcolor: alpha(accentSoft, 0.96),
                transform: "translateY(-1px)",
              },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box sx={{ minWidth: 0, textAlign: "center" }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: { xs: "1.05rem", sm: "1.18rem" } }}>
              {title}
            </Typography>
            <Typography variant="body2" noWrap sx={{ mt: 0.25, color: "text.secondary", fontWeight: 400, fontSize: { xs: "0.76rem", sm: "0.82rem" } }}>
              {childName} · {periodLabel}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            {(showActions ? [
              {
                label: "Print or save PDF",
                action: onDownloadPdf,
                icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
                aria: "Download care team update as PDF",
              },
              {
                label: "Copy care team update",
                action: onCopy,
                icon: <ContentCopyRoundedIcon fontSize="small" />,
                aria: "Copy care team update",
              },
              {
                label: "Share care team update",
                action: onShare,
                icon: <IosShareRoundedIcon fontSize="small" />,
                aria: "Share care team update",
              },
              {
                label: "Open filters",
                action: onOpenFilters,
                icon: <TuneRoundedIcon fontSize="small" />,
                aria: "Open therapy filters",
              },
            ] : []).filter((item) => typeof item.action === "function").map((item) => (
              <Tooltip key={item.label} title={item.label} arrow>
                <IconButton
                  onClick={item.action}
                  size="small"
                  aria-label={item.aria}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: `1px solid ${alpha(accentPrimary, 0.24)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    color: accentInk,
                    "&:hover": {
                      bgcolor: alpha(accentSoft, 0.86),
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {showRangeControls ? (
          <Box sx={{ mt: 1.4, pl: { xs: 0, sm: 8 } }}>
            <DailyCareReportRangeSelector
              rangePreset={rangePreset}
              setRangePreset={setRangePreset}
              customStartDate={customStartDate}
              setCustomStartDate={setCustomStartDate}
              customEndDate={customEndDate}
              setCustomEndDate={setCustomEndDate}
              periodLabel={periodLabel}
            />
          </Box>
        ) : null}
      </Box>
    </Container>
</Box>
  );
};

export default DailyCareReportHeader;
